import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, ArrowLeft, Video, Play, Download, Trash2, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useNavigate } from 'react-router-dom';

interface Category {
  id: string;
  name: string;
  products: Product[];
}

interface Product {
  id: string;
  name: string;
  sku: string;
  aiVideos: VideoFile[];
}

interface VideoFile {
  id: string;
  name: string;
  url: string;
  path: string;
  generatedAt: string;
  size: number;
}

interface MyAIVideosModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MyAIVideosModal: React.FC<MyAIVideosModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentView, setCurrentView] = useState<'categories' | 'products' | 'videos'>('categories');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<VideoFile | null>(null);
  const [pathHistory, setPathHistory] = useState<string[]>([]);

  // Cargar categorías y productos
  const loadCategories = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      console.log('🎬 Cargando categorías para videos IA:', user.uid);
      
      // Usar el mismo servicio que el resto del sistema
      const optimizedService = (await import('../services/optimizedInventoryService')).default;
      const categoriesData = await optimizedService.getCategories();
      
      console.log(`📊 Categorías obtenidas del servicio: ${categoriesData.length}`);
      
      if (categoriesData.length === 0) {
        setError('No se encontraron categorías. Las categorías deben estar definidas en el sistema.');
        setLoading(false);
        return;
      }
      
      // Convertir las categorías del servicio al formato del modal
      // Mostrar todas las categorías (habilitadas y deshabilitadas)
      console.log(`📊 Mostrando todas las categorías: ${categoriesData.length}`);
      
      const categoriesForModal: Category[] = [];
      
      for (const category of categoriesData) {
        // Obtener productos de esta categoría desde Firestore
        const productsRef = window.auth.collection(window.auth.db, 'users', user.uid, 'categories', category.id, 'stock');
        const productsSnapshot = await window.auth.getDocs(productsRef);
        
        console.log(`📦 Productos en ${category.name}: ${productsSnapshot.docs.length}`);
        
        const products: Product[] = [];
        
        for (const productDoc of productsSnapshot.docs) {
          const productData = productDoc.data();
          const sku = productDoc.id;
          
          // Obtener videos IA de este producto desde Storage
          const aiVideos = await loadProductAIVideos(category.id, sku);
          
          products.push({
            id: sku,
            name: productData.name || `Producto ${sku}`,
            sku: sku,
            aiVideos: aiVideos
          });
        }
        
        categoriesForModal.push({
          id: category.id,
          name: category.name,
          products: products
        });
      }
      
      setCategories(categoriesForModal);
      console.log(`✅ Cargadas ${categoriesForModal.length} categorías para videos IA`);
      
    } catch (error) {
      console.error('❌ Error cargando categorías para videos IA:', error);
      setError('Error al cargar las categorías desde el servicio');
    } finally {
      setLoading(false);
    }
  };

  // Cargar videos IA de un producto específico
  const loadProductAIVideos = async (categoryId: string, sku: string): Promise<VideoFile[]> => {
    if (!user || !window.auth?.storage) return [];

    try {
      console.log(`🎬 Cargando videos IA para ${categoryId}/${sku}`);
      
      const aiVideosPath = `users/${user.uid}/categories/${categoryId}/stock/${sku}/vidIA`;
      const aiVideosRef = window.auth.ref(window.auth.storage, aiVideosPath);
      const aiVideosSnapshot = await window.auth.listAll(aiVideosRef);
      
      console.log(`📁 Videos encontrados en ${aiVideosPath}: ${aiVideosSnapshot.items.length}`);
      
      const aiVideos: VideoFile[] = [];
      
      for (const itemRef of aiVideosSnapshot.items) {
        try {
          const url = await window.auth.getDownloadURL(itemRef);
          const metadata = await window.auth.getMetadata(itemRef);
          
          // Detectar si es un video por content type o por extensión
          const fileName = itemRef.name.toLowerCase();
          const contentType = metadata.contentType || '';
          const hasVideoExtension = fileName.endsWith('.mp4') || 
                                   fileName.endsWith('.mov') || 
                                   fileName.endsWith('.avi') || 
                                   fileName.endsWith('.webm') ||
                                   fileName.endsWith('.mkv');
          
          const isVideo = (contentType && contentType.startsWith('video/')) || hasVideoExtension;
          console.log(`🔍 Archivo: ${itemRef.name}, contentType: ${contentType}, extensión: ${hasVideoExtension}, es video: ${isVideo}`);
          
          if (isVideo) {
            aiVideos.push({
              id: itemRef.name,
              name: itemRef.name,
              url: url,
              path: itemRef.fullPath,
              generatedAt: metadata.timeCreated || new Date().toISOString(),
              size: metadata.size || 0
            });
            console.log(`✅ Video IA agregado: ${itemRef.name}`);
          }
        } catch (err) {
          console.warn('⚠️ Error obteniendo URL para video IA:', itemRef.name, err);
        }
      }
      
      console.log(`✅ ${aiVideos.length} videos IA cargados`);
      return aiVideos;
    } catch (error) {
      console.error('❌ Error cargando videos IA del producto:', error);
      return [];
    }
  };

  // Filtrar categorías por término de búsqueda
  const filteredCategories = categories.filter(category => {
    if (!searchTerm) return true;
    
    const categoryMatch = category.name.toLowerCase().includes(searchTerm.toLowerCase());
    const productMatch = category.products.some(product => 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return categoryMatch || productMatch;
  });

  // Navegar a productos de una categoría
  const navigateToProducts = (category: Category) => {
    setSelectedCategory(category);
    setCurrentView('products');
    setPathHistory([...pathHistory, 'categories']);
  };

  // Navegar a videos IA de un producto
  const navigateToVideos = (product: Product) => {
    setSelectedProduct(product);
    setCurrentView('videos');
    setPathHistory([...pathHistory, 'products']);
  };

  // Volver atrás
  const goBack = () => {
    if (pathHistory.length === 0) {
      setCurrentView('categories');
      setSelectedCategory(null);
      setSelectedProduct(null);
    } else if (currentView === 'videos') {
      setCurrentView('products');
      setSelectedProduct(null);
      const newHistory = [...pathHistory];
      newHistory.pop();
      setPathHistory(newHistory);
    } else if (currentView === 'products') {
      setCurrentView('categories');
      setSelectedCategory(null);
      const newHistory = [...pathHistory];
      newHistory.pop();
      setPathHistory(newHistory);
    }
  };

  // Reproducir video
  const playVideo = (video: VideoFile) => {
    setSelectedVideo(video);
  };

  // Descargar video
  const downloadVideo = async (video: VideoFile) => {
    try {
      const response = await fetch(video.url);
      const blob = await response.blob();
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = video.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('❌ Error descargando video IA:', error);
    }
  };

  // Eliminar video IA
  const deleteVideo = async (video: VideoFile) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este video generado con IA?')) return;

    try {
      const videoRef = window.auth.storage.ref(video.path);
      await window.auth.storage.deleteObject(videoRef);
      
      // Recargar datos
      await loadCategories();
      
      // Si estamos viendo videos de un producto, recargar esos videos específicamente
      if (selectedProduct) {
        const updatedVideos = await loadProductAIVideos(selectedCategory!.id, selectedProduct.sku);
        setSelectedProduct({
          ...selectedProduct,
          aiVideos: updatedVideos
        });
      }
      
    } catch (error) {
      console.error('❌ Error eliminando video IA:', error);
      alert('Error al eliminar el video');
    }
  };

  // Ir a generar con IA
  const goToGenerateWithAI = () => {
    onClose();
    navigate('/generate-with-ai');
  };

  // Formatear tamaño de archivo
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Cargar datos al abrir el modal
  useEffect(() => {
    if (isOpen) {
      loadCategories();
      setCurrentView('categories');
      setSelectedCategory(null);
      setSelectedProduct(null);
      setSelectedVideo(null);
      setPathHistory([]);
      setSearchTerm('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-4">
              {currentView !== 'categories' && (
                <button
                  onClick={goBack}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Mis Videos IA
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {currentView === 'categories' && 'Selecciona una categoría'}
                  {currentView === 'products' && selectedCategory && `Productos en ${selectedCategory.name}`}
                  {currentView === 'videos' && selectedProduct && `Videos IA de ${selectedProduct.name}`}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por nombre de producto o SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {loading && (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                <span className="ml-2 text-gray-600 dark:text-gray-300">Cargando...</span>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-red-500 text-lg mb-2">❌</div>
                  <p className="text-red-600 dark:text-red-400">{error}</p>
                  <button
                    onClick={loadCategories}
                    className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    Reintentar
                  </button>
                </div>
              </div>
            )}

            {!loading && !error && (
              <div className="h-full overflow-y-auto p-6">
                {currentView === 'categories' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredCategories.map((category) => (
                      <motion.div
                        key={category.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        onClick={() => navigateToProducts(category)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                            <Video className="w-6 h-6 text-red-500" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 dark:text-gray-100">
                              {category.name}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {category.products.length} productos
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                {currentView === 'products' && selectedCategory && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {selectedCategory.products.map((product) => (
                      <motion.div
                        key={product.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        onClick={() => navigateToVideos(product)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                            <span className="text-green-500 text-lg">📦</span>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 dark:text-gray-100">
                              {product.name}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              SKU: {product.sku}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {product.aiVideos.length} videos IA
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                {currentView === 'videos' && selectedProduct && (
                  <div>
                    {selectedProduct.aiVideos.length === 0 ? (
                      <div className="text-center py-12">
                        <Video className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                          No tenés videos generados con IA
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-6">
                          Genera videos con IA para este producto
                        </p>
                        <button
                          onClick={goToGenerateWithAI}
                          className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center space-x-2 mx-auto"
                        >
                          <Video className="w-5 h-5" />
                          <span>Generar con IA</span>
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {selectedProduct.aiVideos.map((video) => (
                          <motion.div
                            key={video.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="relative group bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden"
                          >
                            <div className="aspect-video relative">
                              <video
                                src={video.url}
                                className="w-full h-full object-cover"
                                poster=""
                                preload="metadata"
                              />
                              
                              {/* Badge de IA */}
                              <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full flex items-center space-x-1">
                                <Sparkles className="w-3 h-3" />
                                <span>IA</span>
                              </div>
                              
                              {/* Botón de play */}
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    playVideo(video);
                                  }}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-90 rounded-full p-3 hover:bg-opacity-100"
                                >
                                  <Play className="w-6 h-6 text-gray-700 ml-1" />
                                </button>
                              </div>
                              
                              {/* Overlay de acciones */}
                              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadVideo(video);
                                  }}
                                  className="p-2 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-colors"
                                >
                                  <Download className="w-4 h-4 text-gray-700" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteVideo(video);
                                  }}
                                  className="p-2 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-colors"
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </button>
                              </div>
                            </div>
                            
                            <div className="p-4">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate mb-1">
                                {video.name}
                              </p>
                              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                                <span>{new Date(video.generatedAt).toLocaleDateString()}</span>
                                <span>{formatFileSize(video.size)}</span>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Modal de reproducción de video */}
        <AnimatePresence>
          {selectedVideo && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-60 p-4"
              onClick={() => setSelectedVideo(null)}
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                className="relative max-w-4xl max-h-[90vh] w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <video
                  src={selectedVideo.url}
                  controls
                  autoPlay
                  className="w-full h-full max-h-[80vh] object-contain rounded-lg"
                />
                <button
                  onClick={() => setSelectedVideo(null)}
                  className="absolute top-4 right-4 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default MyAIVideosModal;
